import {
    Modal,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Box,
    IconButton
} from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import SendIcon from '@mui/icons-material/Send';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { ImagePaths } from '../../Utility/ImagePaths';
import RestAPIService from '../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../Utility/API/Serviceurls';
import { localGetItem } from '../../Utility/Services/CacheProviderService';

const AIaudioToText = ({ ClosePopUp, transformText, PatientData }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [audioBlob, setAudioBlob] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);

    // Timer effect
    useEffect(() => {
        if (isRecording) {
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }

        return () => clearInterval(timerRef.current);
    }, [isRecording]);

    const startRecording = async () => {
        try {
            setError(null);
            setRecordingTime(0);
            audioChunksRef.current = [];

            if (!window.MediaRecorder) {
                setError("Audio recording is not supported in this browser.");
                return;
            }

            const stream = await navigator?.mediaDevices?.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = async () => {
                const recordedBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const wavBlob = await convertToWav(recordedBlob);
                setAudioBlob(wavBlob);
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (err) {
            setError("Could not access microphone. Please check permissions.");
            console.error("Recording error:", err);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.requestData(); // flush last chunk
            mediaRecorderRef.current.stop();
            stopMicStream();
            setIsRecording(false);
        }
    };

    const stopMicStream = () => {
        if (mediaRecorderRef.current?.stream) {
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
    };

    const generateTranscript = async () => {
        if (!audioBlob) {
            setError("No audio recorded. Please record first.");
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('doctor_id', (PatientData?.doctor_id ? PatientData?.doctor_id : null));
            formData.append('appointment_id', (PatientData?.appointment_id ? PatientData?.appointment_id : null));
            formData.append('clinic_id', (localGetItem("SelectedDoctorClinicId")));
            formData.append('audio', audioBlob, 'recording.wav'); // wav file
            formData.append('time', new Date().toISOString());

            let response = await RestAPIService.create(
                formData,
                Serviceurls.AI_DOCTOR_NOTES_TEXT_CONVERT,
                { headers: { 'Content-Type': 'multipart/form-data' } } // ensure multipart
            );

            if (response.data?.status === 'success') {
                await transformText(response.data?.data || "");
            } else {
                setError(response.data?.message || "Failed to process audio. Please try again.");
            }
        } catch (err) {
            setError("Failed to process audio. Please try again.");
            console.error("API error:", err);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        if (isRecording) stopRecording();
        ClosePopUp();
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // ===== Helper: Convert to WAV =====
    const convertToWav = async (blob) => {
        const arrayBuffer = await blob.arrayBuffer();
        const audioCtx = new AudioContext();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        const wavBuffer = audioBufferToWav(audioBuffer);
        return new Blob([wavBuffer], { type: 'audio/wav' });
    };

    function audioBufferToWav(buffer) {
        let numOfChan = buffer.numberOfChannels,
            length = buffer.length * numOfChan * 2 + 44,
            bufferArray = new ArrayBuffer(length),
            view = new DataView(bufferArray),
            channels = [],
            sample,
            offset = 0,
            pos = 0;

        // write WAVE header
        setUint32(0x46464952);                         // "RIFF"
        setUint32(length - 8);                         // file length - 8
        setUint32(0x45564157);                         // "WAVE"
        setUint32(0x20746d66);                         // "fmt " chunk
        setUint32(16);                                 // length = 16
        setUint16(1);                                  // PCM (uncompressed)
        setUint16(numOfChan);
        setUint32(buffer.sampleRate);
        setUint32(buffer.sampleRate * 2 * numOfChan);  // avg. bytes/sec
        setUint16(numOfChan * 2);                      // block-align
        setUint16(16);                                 // 16-bit
        setUint32(0x61746164);                         // "data" - chunk
        setUint32(length - pos - 4);

        // write interleaved data
        for (let i = 0; i < buffer.numberOfChannels; i++)
            channels.push(buffer.getChannelData(i));

        while (pos < length) {
            for (let i = 0; i < numOfChan; i++) { // interleave channels
                sample = Math.max(-1, Math.min(1, channels[i][offset])); // clamp
                view.setInt16(pos, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
                pos += 2;
            }
            offset++;
        }

        return bufferArray;

        function setUint16(data) { view.setUint16(pos, data, true); pos += 2; }
        function setUint32(data) { view.setUint32(pos, data, true); pos += 4; }
    }

    return (
        <Modal open={true} onClose={handleClose}>
            <Paper elevation={3} sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                maxWidth: 400,
                p: 3,
                outline: 'none',
                textAlign: 'center',
                height: '30vh',
            }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <IconButton color="primary" curser="pointer" onClick={() => handleClose()} >
                        <img style={{ width: '2vw', height: '2vh' }} src={ImagePaths.Close.default} alt='close' />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                    {error && (
                        <Typography display={error ? 'block' : 'none'} color="error" variant="body2">{error}</Typography>
                    )}

                    {/* Recording Animation */}
                    <Box sx={{
                        visibility: isRecording ? 'visible' : 'hidden',
                        display: isRecording ? 'flex' : 'none',
                        alignItems: 'center',
                        gap: 1,
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                            '0%': { opacity: 0.6 },
                            '50%': { opacity: 1 },
                            '100%': { opacity: 0.6 }
                        }
                    }}>
                        <FiberManualRecordIcon color="error" fontSize="large" />
                        <Typography variant="h6">{formatTime(recordingTime)}</Typography>
                    </Box>

                    {isProcessing ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <img className="eMed_Bill_Success_animation" src={ImagePaths.AiVoiceAnimation.default} />
                            <Typography>Processing audio...</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {!isRecording ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<MicIcon />}
                                    onClick={startRecording}
                                    disabled={isProcessing}
                                    size="small"
                                >
                                    Record
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<StopIcon />}
                                    onClick={stopRecording}
                                    size="small"
                                >
                                    Stop
                                </Button>
                            )}

                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<SendIcon />}
                                onClick={generateTranscript}
                                disabled={!audioBlob || isRecording}
                                size="small"
                            >
                                Process Audio
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Modal>
    );
};

export default AIaudioToText;
